<template>
  <div class="input-field" :class="{ 'full-width': fullWidth }">
    <label class="input-title">{{ label }}</label>
    <div class="input-box" :class="{ 'money-input': moneyType }">
      <span v-if="moneyType" class="money-prefix">$</span>
      <input
        :id="id"
        :value="inputValue"
        :type="type"
        :placeholder="placeholder"
        :required="required"
        :disabled="disabled"
        :maxlength="maxlength"
        class="input-text"
        :class="{ 'money-input-field': moneyType }"
        @input="handleInput"
        @keypress="handleKeyPress"
        @blur="handleBlur"
      />
      <InfoButton
        v-if="showError && errorMessage"
        :messages="[errorMessage]"
        :type="errorType"
        class="input-info-button"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick } from 'vue'
import InfoButton from './InfoButton.vue'

interface Props {
  id: string
  modelValue: string
  label: string
  type?: string
  placeholder?: string
  required?: boolean
  disabled?: boolean
  fullWidth?: boolean
  uppercase?: boolean
  maxlength?: number
  showError?: boolean
  errorMessage?: string
  errorType?: string
  numericOnly?: boolean
  moneyType?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  type: 'text',
  placeholder: '',
  required: false,
  disabled: false,
  fullWidth: false,
  uppercase: false,
  showError: false,
  errorMessage: '',
  errorType: 'error',
  numericOnly: false,
  moneyType: false,
})

const emit = defineEmits<{
  'update:modelValue': [value: string]
  blur: []
}>()

const inputValue = computed(() => props.modelValue)

const handleKeyPress = (event: KeyboardEvent) => {
  // Only allow numeric input if numericOnly or moneyType is true
  if (props.numericOnly || props.moneyType) {
    const char = String.fromCharCode(event.which)
    const currentValue = (event.target as HTMLInputElement).value

    // Allow numbers, decimal point, and backspace/delete
    if (!/[0-9.]/.test(char) && event.key !== 'Backspace' && event.key !== 'Delete') {
      event.preventDefault()
      return
    }

    // For money type, prevent multiple decimal points
    if (props.moneyType && char === '.') {
      if (currentValue.includes('.')) {
        event.preventDefault()
        return
      }
    }

    // For money type, prevent more than 2 decimal places
    if (props.moneyType && char !== '.' && currentValue.includes('.')) {
      const decimalPart = currentValue.split('.')[1]
      if (decimalPart && decimalPart.length >= 2) {
        event.preventDefault()
        return
      }
    }
  }
}

const handleInput = async (event: Event) => {
  const target = event.target as HTMLInputElement
  let value = target.value

  // Handle money type formatting
  if (props.moneyType) {
    // Remove any non-numeric characters except decimal point
    value = value.replace(/[^0-9.]/g, '')

    // Ensure only one decimal point
    const parts = value.split('.')
    if (parts.length > 2) {
      value = parts[0] + '.' + parts.slice(1).join('')
    }

    // Limit to 2 decimal places - more strict enforcement
    if (parts.length === 2) {
      if (parts[1].length > 2) {
        value = parts[0] + '.' + parts[1].substring(0, 2)
      }
    }

    // Update the input value to reflect the changes
    if (target.value !== value) {
      target.value = value
    }
  }

  // Remove non-numeric characters if numericOnly is true
  if (props.numericOnly && !props.moneyType) {
    value = value.replace(/[^0-9]/g, '')
  }

  if (props.uppercase) {
    value = value.toUpperCase()
    await nextTick()
    target.value = value
  }

  emit('update:modelValue', value)
}

const handleBlur = (event: Event) => {
  const target = event.target as HTMLInputElement
  let value = target.value

  // Format money value on blur
  if (props.moneyType && value) {
    const numericValue = parseFloat(value)
    if (!isNaN(numericValue)) {
      // Format to 2 decimal places
      value = numericValue.toFixed(2)
      target.value = value
      emit('update:modelValue', value)
    }
  }

  // Emit blur event
  emit('blur')
}
</script>

<style scoped>
.input-field {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  gap: 8px;
  width: 544px;
  height: 70px;
  flex: none;
  order: 0;
  flex-grow: 1;
}

.input-field.full-width {
  width: 100%;
}

.input-title {
  width: auto;
  height: 22px;
  font-family: 'Montserrat', sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 140%;
  color: #2b2a2a;
  margin: 0;
}

.input-box {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  gap: 4px;
  width: 100%;
  height: 48px;
  flex: none;
  order: 1;
  align-self: stretch;
  flex-grow: 0;
  position: relative;
}

.input-text {
  width: 100%;
  height: 48px;
  background: #f2f2f2;
  border-radius: 16px;
  border: none;
  padding: 0 16px;
  font-family: 'Montserrat', sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 140%;
  color: #2b2a2a;
  outline: none;
  box-sizing: border-box;
}

.input-text::placeholder {
  color: #a1a1a1;
}

.input-text:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Money input styles */
.money-input {
  position: relative;
}

.money-prefix {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  font-family: 'Montserrat', sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 140%;
  color: #2b2a2a;
  z-index: 2;
  pointer-events: none;
}

.money-input-field {
  padding-left: 32px !important;
}

/* InfoButton positioned inside the input field */
.input-info-button {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
}
</style>
